
import React, { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import Index from "./pages/Index";
import AuthLayout from "./components/AuthLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import RouteErrorBoundary from "./components/performance/RouteErrorBoundary";
import { ROLES } from "./constants/roles";

// Lazy-loaded pages for code splitting
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ListProperty = lazy(() => import("./pages/ListProperty"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const MarketingFlyer = lazy(() => import("./pages/MarketingFlyer"));
const RentPrediction = lazy(() => import("./pages/RentPrediction"));
const DistrictRentPrediction = lazy(() => import("./pages/DistrictRentPrediction"));
const ForTenants = lazy(() => import("./pages/ForTenants"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const LandlordDashboard = lazy(() => import("./pages/LandlordDashboard"));
const TenantDashboard = lazy(() => import("./pages/TenantDashboard"));
const PropertyBrowsing = lazy(() => import("./pages/PropertyBrowsing"));

const PageLoader = () => (
  <div className="min-h-screen flex justify-center items-center">
    <div className="critical-spinner"></div>
  </div>
);

const Lazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>
    <RouteErrorBoundary>{children}</RouteErrorBoundary>
  </Suspense>
);

export const routes = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [
      {
        path: "/",
        element: <Index />,
      },
      {
        path: "/about",
        element: <Lazy><AboutUs /></Lazy>,
      },
      {
        path: "/list-property",
        element: <Lazy><ListProperty /></Lazy>,
      },
      {
        path: "/privacy-policy",
        element: <Lazy><PrivacyPolicy /></Lazy>,
      },
      {
        path: "/refund-policy",
        element: <Lazy><RefundPolicy /></Lazy>,
      },
      {
        path: "/contact-us",
        element: <Lazy><Contact /></Lazy>,
      },
      {
        path: "/terms-of-use",
        element: <Lazy><TermsOfUse /></Lazy>,
      },
      {
        path: "/admin-settings",
        element: (
          <Lazy>
            <ProtectedRoute requiredRole={ROLES.ADMIN}>
              <AdminSettings />
            </ProtectedRoute>
          </Lazy>
        ),
      },
      {
        path: "/marketing-flyer",
        element: <Lazy><MarketingFlyer /></Lazy>,
      },
      {
        path: "/rent-predictor",
        element: <Lazy><RentPrediction /></Lazy>,
      },
      {
        path: "/du-tinh-gia-thue/:districtSlug",
        element: <Lazy><DistrictRentPrediction /></Lazy>,
      },
      {
        path: "/du-tinh-gia-thue/:districtSlug/:buildingSlug",
        element: <Lazy><DistrictRentPrediction /></Lazy>,
      },
      {
        path: "/tenants",
        element: <Lazy><ForTenants /></Lazy>,
      },
      {
        path: "/properties",
        element: <Lazy><PropertyBrowsing /></Lazy>,
      },
      {
        path: "/auth",
        element: <Lazy><Auth /></Lazy>,
      },
      {
        path: "/admin",
        element: (
          <Lazy>
            <ProtectedRoute requiredRole={ROLES.ADMIN}>
              <AdminDashboard />
            </ProtectedRoute>
          </Lazy>
        ),
      },
      {
        path: "/landlord",
        element: (
          <Lazy>
            <ProtectedRoute requiredRole={ROLES.LANDLORD}>
              <LandlordDashboard />
            </ProtectedRoute>
          </Lazy>
        ),
      },
      {
        path: "/tenant",
        element: (
          <Lazy>
            <ProtectedRoute requiredRole={ROLES.TENANT}>
              <TenantDashboard />
            </ProtectedRoute>
          </Lazy>
        ),
      },
      {
        path: "*",
        element: <Lazy><NotFound /></Lazy>,
      },
    ],
  },
]);
